import React, { useEffect, useState } from "react";
import { AlertTriangle, BedDouble } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

export default function HospitalOps() {
  const [orSchedule, setOR] = useState([]);
  const [beds, setBeds] = useState(null);
  const [cred, setCred] = useState([]);
  const [equip, setEquip] = useState([]);

  useEffect(() => {
    api.get("/hospital/or-schedule").then(r=>setOR(r.data));
    api.get("/hospital/beds").then(r=>setBeds(r.data));
    api.get("/hospital/credentialing").then(r=>setCred(r.data));
    api.get("/hospital/equipment").then(r=>setEquip(r.data));
  }, []);

  const orCols = [
    { key: "time", label: "Time" },
    { key: "room", label: "Room" },
    { key: "surgeon", label: "Surgeon" },
    { key: "case", label: "Case" },
    { key: "duration_min", label: "Duration", render: (r)=>`${r.duration_min} min` },
  ];

  const credCols = [
    { key: "physician", label: "Physician" },
    { key: "credential", label: "Credential" },
    { key: "expires", label: "Expires" },
    { key: "days_until_expiration", label: "Days", render: (r)=>(
      <span className={r.alert?"text-red-600 font-semibold":""}>{r.days_until_expiration} {r.alert && <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />}</span>
    )},
  ];

  const equipCols = [
    { key: "name", label: "Equipment" },
    { key: "status", label: "Status", render: (r)=><span className={`status-pill ${r.status==="operational"?"status-approved":r.status==="maintenance"?"status-pending":"status-submitted"}`}>{r.status.replace("_"," ")}</span> },
    { key: "next_maintenance", label: "Next Maint." },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 11" title="Hospital Operations"
        subtitle="OR scheduling, bed management, physician credentialing and equipment availability." testid="hospital-page" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {beds && (
          <>
            <Cell label="Total Beds" value={beds.total} />
            <Cell label="Occupied" value={beds.occupied} accent="amber" />
            <Cell label="Available" value={beds.available} accent="teal" />
            <Cell label="Predicted Discharges" value={beds.predicted_discharges} />
          </>
        )}
      </div>

      {beds && (
        <SectionCard title="ICU" testid="icu-card">
          <div className="flex items-center gap-6">
            <BedDouble className="w-8 h-8 text-brand-primary" />
            <div>
              <div className="text-2xl font-semibold">{beds.icu_occupied} / {beds.icu_total}</div>
              <div className="text-xs text-brand-muted">ICU beds occupied — {beds.icu_total - beds.icu_occupied} available</div>
            </div>
            <div className="flex-1 h-3 bg-brand-bg rounded-full overflow-hidden ml-4">
              <div className="h-full bg-brand-primary" style={{width: `${(beds.icu_occupied/beds.icu_total)*100}%`}}></div>
            </div>
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="OR Schedule Board" testid="or-section">
          <DataTable data={orSchedule} columns={orCols} searchKeys={["surgeon","case","room"]} testid="or-table" />
        </SectionCard>
        <SectionCard title="Credentialing Tracker" testid="cred-section">
          <DataTable data={cred} columns={credCols} searchKeys={["physician","credential"]} testid="cred-table" />
        </SectionCard>
      </div>

      <div className="mt-6">
        <SectionCard title="Equipment" testid="equip-section">
          <DataTable data={equip} columns={equipCols} searchKeys={["name","status"]} testid="equip-table" />
        </SectionCard>
      </div>
    </>
  );
}

const Cell = ({ label, value, accent }) => (
  <div className="kpi-card">
    <div className="eyebrow">{label}</div>
    <div className={`text-3xl font-semibold mt-2 ${accent==="amber"?"text-amber-600":accent==="teal"?"text-brand-teal":"text-brand-text"}`}>{value}</div>
  </div>
);
